<template>
  <div class="error-page">
    <div class="error-content">
      <h1 v-html="errorTitle"></h1>
      <p>{{ errorText }}</p>
      <div v-if="isDev && error.statusCode === 500" class="error-details">
        <pre>{{ errorDetails }}</pre>
      </div>
      <button @click="goHome">{{ t('error.to_main') }}</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { computed } from 'vue'

const { t } = useI18n({ useScope: 'local' })
const router = useRouter()

// Get error object from Nuxt props
const props = defineProps({
  error: {
    type: Object,
    default: () => ({ statusCode: 500 })
  }
})

// Check if we're in development mode
const isDev = process.env.NODE_ENV === 'development'

// Format error details for display
const errorDetails = computed(() => {
  if (!props.error) return ''
  return JSON.stringify(props.error, null, 2)
})

const errorTitle = computed(() => {
  switch (props.error.statusCode) {
    case 404:
      return t('error.404_title')
    case 500:
      return t('error.500_title')
    case 403:
      return t('error.403_title')
    case 401:
      return t('error.401_title')
    default:
      return t('error.default_title')
  }
})

const errorText = computed(() => {
  switch (props.error.statusCode) {
    case 404:
      return t('error.404_text')
    case 500:
      return t('error.500_text')
    case 403:
      return t('error.403_text')
    case 401:
      return t('error.401_text')
    default:
      return t('error.default_text')
  }
})

function goHome() {
  router.push('/')
}
</script>

<i18n lang="json">
{
  "ru": {
    "error": {
      "404_title": "Ошибка 404.\nСтраница не найдена",
      "404_text": "Вы попали на несуществующую страницу. Возможно, неправильно набран адрес или такой страницы на сайте больше не существует.",
      "500_title": "Ошибка 500.\nВнутренняя ошибка сервера",
      "500_text": "На сервере произошла ошибка. Пожалуйста, попробуйте позже или обратитесь в поддержку.",
      "403_title": "Ошибка 403.\nДоступ запрещён",
      "403_text": "У вас нет прав для просмотра этой страницы.",
      "401_title": "Ошибка 401.\nТребуется авторизация",
      "401_text": "Для доступа к этой странице необходимо войти в систему.",
      "default_title": "Произошла ошибка",
      "default_text": "Что-то пошло не так. Пожалуйста, попробуйте позже.",
      "to_main": "На главную"
    }
  },
  "en": {
    "error": {
      "404_title": "404 error.\nPage not found",
      "404_text": "You have reached a non-existent page. The address may be incorrect or this page no longer exists.",
      "500_title": "500 error.\nInternal server error",
      "500_text": "An error occurred on the server. Please try again later or contact support.",
      "403_title": "403 error.\nAccess denied",
      "403_text": "You do not have permission to view this page.",
      "401_title": "401 error.\nAuthorization required",
      "401_text": "You need to be logged in to access this page.",
      "default_title": "An error occurred",
      "default_text": "Something went wrong. Please try again later.",
      "to_main": "Go to main page"
    }
  }
}
</i18n>

<style scoped>
.error-page {
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-content {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 0 0 1px #ececec;
  padding: 48px 32px;
  max-width: 480px;
  width: 100%;
  text-align: center;
  border: 1px solid #ececec;
}

h1 {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 16px;
  color: #222;
}

p {
  color: #222;
  font-size: 1rem;
  margin-bottom: 32px;
}

button {
  background: #111;
  color: #fff;
  border: none;
  border-radius: 24px;
  padding: 10px 32px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}
button:hover {
  background: #333;
}

.error-details {
  margin: 24px 0;
  padding: 16px;
  background: #f5f5f5;
  border-radius: 8px;
  text-align: left;
  overflow-x: auto;
}

.error-details pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: monospace;
  font-size: 0.875rem;
  color: #333;
}
</style> 